import { useEffect, useState, useCallback, useRef } from 'react';

// ============================================
// Types
// ============================================

export type ToastType = 'error' | 'success' | 'info';

export interface ToastMessage {
  id: number;
  type: ToastType;
  message: string;
}

// ============================================
// Global event bus (no context needed)
// ============================================

type Listener = (toast: Omit<ToastMessage, 'id'>) => void;
const listeners: Set<Listener> = new Set();

export function showToast(message: string, type: ToastType = 'error') {
  listeners.forEach((fn) => fn({ message, type }));
}

// ============================================
// Single Toast Item
// ============================================

function ToastItem({ toast, onRemove }: { toast: ToastMessage; onRemove: (id: number) => void }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Trigger enter animation
    const enterTimer = setTimeout(() => setVisible(true), 10);
    // Auto-dismiss after 4s
    const exitTimer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onRemove(toast.id), 300);
    }, 4000);
    return () => { clearTimeout(enterTimer); clearTimeout(exitTimer); };
  }, [toast.id, onRemove]);

  const colors = {
    error: 'bg-red-600',
    success: 'bg-emerald-600',
    info: 'bg-blue-600',
  };

  const icons = {
    error: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0">
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" />
        <path d="M12 8v4M12 16h.01" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    success: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0">
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" />
        <path d="M8 12l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    info: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0">
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" />
        <path d="M12 11v5M12 8h.01" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  };

  return (
    <div
      className={`flex items-start gap-3 rounded-xl px-4 py-3 text-sm text-white shadow-lg transition-all duration-300 ${colors[toast.type]} ${
        visible ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
      }`}
      style={{ maxWidth: 360 }}
    >
      {icons[toast.type]}
      <span className="flex-1 leading-snug">{toast.message}</span>
      <button
        onClick={() => { setVisible(false); setTimeout(() => onRemove(toast.id), 300); }}
        className="ml-1 shrink-0 opacity-70 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

// ============================================
// Toast Container — mount once in App/Layout
// ============================================

let idCounter = 0;

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const listenerRef = useRef<Listener | null>(null);

  useEffect(() => {
    const listener: Listener = (toast) => {
      setToasts((prev) => [...prev, { ...toast, id: ++idCounter }]);
    };
    listenerRef.current = listener;
    listeners.add(listener);
    return () => { if (listenerRef.current) listeners.delete(listenerRef.current); };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-2 items-end pointer-events-none">
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem toast={t} onRemove={removeToast} />
        </div>
      ))}
    </div>
  );
}
